import { useState } from "react";
import "./ServicesPage.css";
import { useAuth } from "../../../hooks/useAuth";
import { useServices } from "../../../hooks/useServices";
import { useBranches } from "../../../hooks/useBranches";
import {
  createServiceAsync,
  deleteServiceAsync,
  updateServiceAsync,
} from "../../../api/ServiceApi";
import {
  CreateServiceRequest,
  Service,
  ServiceUpdateModel,
} from "../../../models/ServiceModels";
import { Constants } from "../../../constants/constants";
import { OrgForms } from "../../../config/forms";
import ServiceModal, {
  ServiceFormValues,
  ServiceModalMode,
} from "./ServiceModal";
import Toast from "../../../components/Toast";

interface IconProps {
  title?: string;
}

const EditIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

const DeleteIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const toBranchAssignments = (form: ServiceFormValues) =>
  form.branchIds.map((branchId) => ({ branchId, isActive: true }));

const toUpdateRequest = (form: ServiceFormValues): ServiceUpdateModel => ({
  name: form.name || undefined,
  description: form.description || undefined,
  category: form.category || undefined,
  durationMinutes: Number(form.durationMinutes) || 30,
  price: Number(form.price) || 0,
  currency: form.currency || undefined,
  isActive: form.isActive,
  sortOrder: Number(form.sortOrder) || 0,
  imageFile: form.imageFile || null,
  branches: toBranchAssignments(form),
});

const ServicesPage = () => {
  const { user, hasPrivilege } = useAuth();
  const organizationId = user?.activeOrganization?.organizationId;
  const canCreateServices = hasPrivilege(OrgForms.Services, "canCreate");
  const canEditServices = hasPrivilege(OrgForms.Services, "canEdit");
  const canDeleteServices = hasPrivilege(OrgForms.Services, "canDelete");

  const {
    services,
    loading,
    error,
    reload,
    addLocal,
    removeLocal,
  } = useServices(organizationId);

  const { branches } = useBranches(organizationId);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ServiceModalMode>("create");
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const openModal = (mode: ServiceModalMode, service: Service | null) => {
    setModalMode(mode);
    setSelectedService(service);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleSubmit = async (form: ServiceFormValues) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      if (modalMode === "create") {
        if (!organizationId) {
          setToast({ message: "No active organization", type: "error" });
          return;
        }
        const payload: CreateServiceRequest = {
          organizationId,
          name: form.name,
          description: form.description || undefined,
          category: form.category || undefined,
          durationMinutes: Number(form.durationMinutes) || 30,
          price: Number(form.price) || 0,
          currency: form.currency || "BDT",
          isActive: form.isActive,
          sortOrder: Number(form.sortOrder) || 0,
          imageFile: form.imageFile || null,
          branches: toBranchAssignments(form),
        };
        const res = await createServiceAsync(token, payload);
        if (res.success && res.result) {
          addLocal(res.result);
          closeModal();
          setToast({
            message: "Service created successfully!",
            type: "success",
          });
        } else {
          setToast({
            message: res.message || "Failed to create service",
            type: "error",
          });
        }
        return;
      }

      if (modalMode === "edit" && selectedService) {
        const res = await updateServiceAsync(
          token,
          selectedService.id,
          toUpdateRequest(form),
        );
        if (res.success) {
          await reload();
          closeModal();
          setToast({
            message: "Service updated successfully!",
            type: "success",
          });
        } else {
          setToast({
            message: res.message || "Failed to update service",
            type: "error",
          });
        }
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Something went wrong. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (service: Service) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      const res = await deleteServiceAsync(token, service.id);
      if (res.success) {
        removeLocal(service.id);
        setToast({
          message: "Service deleted successfully!",
          type: "success",
        });
      } else {
        setToast({
          message: res.message || "Failed to delete service",
          type: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Failed to delete service. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (loading) {
      return <div className="services-empty">Loading services...</div>;
    }

    if (error) {
      return (
        <div className="services-empty">
          <p>{error}</p>
          <button
            className="services-btn services-btn--secondary"
            onClick={reload}
          >
            Retry
          </button>
        </div>
      );
    }

    if (services.length === 0) {
      return (
        <div className="services-empty">
          No services found. Click &quot;Add Service&quot; to create one.
        </div>
      );
    }

    return (
      <div className="services-table-wrap">
        <table className="services-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Price</th>
              <th>Duration</th>
              <th>Status</th>
              <th>Branches</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {services.map((service) => (
              <tr key={service.id}>
                <td>
                  <div className="services-name">
                    <span className="services-avatar">
                      {service.name.charAt(0).toUpperCase()}
                    </span>
                    {service.name}
                  </div>
                </td>
                <td>{service.category || "—"}</td>
                <td>
                  {service.currency} {service.price}
                </td>
                <td>{service.durationMinutes} min</td>
                <td>
                  <span
                    className={`services-badge ${
                      service.isActive ? "active" : "inactive"
                    }`}
                  >
                    {service.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>{service.branches?.length ?? 0}</td>
                <td className="services-actions">
                  {canEditServices && (
                    <button
                      className="services-icon-btn services-icon-btn--edit"
                      title="Edit"
                      aria-label={`Edit service "${service.name}"`}
                      onClick={() => openModal("edit", service)}
                    >
                      <EditIcon />
                    </button>
                  )}
                  {canDeleteServices && (
                    <button
                      className="services-icon-btn services-icon-btn--delete"
                      title="Delete"
                      aria-label={`Delete service "${service.name}"`}
                      onClick={() => {
                        if (window.confirm(`Delete "${service.name}"?`)) {
                          handleDelete(service);
                        }
                      }}
                    >
                      <DeleteIcon />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="services-page">
      <div className="services-header">
        <div>
          <h2>Services</h2>
          <p>
            Manage services for{" "}
            {user?.activeOrganization?.name ?? "your organization"}
          </p>
        </div>
        {canCreateServices && (
          <button
            className="services-btn services-btn--primary"
            onClick={() => openModal("create", null)}
          >
            + Add Service
          </button>
        )}
      </div>

      {renderBody()}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <ServiceModal
        isOpen={modalOpen}
        mode={modalMode}
        service={selectedService}
        branches={branches}
        submitting={submitting}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default ServicesPage;
